import { AppContext } from '@/app/appContext';
import { NewIntercomMessenger } from '@/app/components/intercom/NewIntercomMessenger';
import { createFileRoute, notFound, Outlet, useMatches } from '@tanstack/react-router';
import { use } from 'react';
import {
  Sidebar,
  SidebarAccordion,
  SidebarAccordionItem,
  SidebarAccordionPanel,
  SidebarAccordionTrigger,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderButton,
  SidebarHeading,
  SidebarItem,
  SidebarLink,
  SidebarList,
  SidebarOrganizationMenu,
  SidebarTrigger,
  SidebarUserMenu,
} from '@/app/ui/components/sidebar';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowDownIcon,
  ArrowTurnBackwardIcon,
  ArrowUpIcon,
  Building06Icon,
  Chart01Icon,
  InboxIcon,
  Search01Icon,
  Task02Icon,
} from '@hugeicons/core-free-icons';
import {
  BottomNavigation,
  BottomNavigationLink,
  BottomNavigationList,
} from '@/app/ui/components/bottom-navigation';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { ProjectTypeLabelsEnum } from '@/app/utils/constants';
import { Spinner } from '@/app/ui/components/spinner';
import { AxiosError } from 'axios';
import { EvaluationSidebarSectionContent } from '@/app/components/features/evaluations/EvaluationSidebarSectionContent';
import * as React from 'react';
import {
  Command,
  CommandCollection,
  CommandDialog,
  CommandDialogPopup,
  CommandDialogTrigger,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from '@/app/ui/components/command';
import { useTranslations } from 'use-intl';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useGetEvaluationsList } from '@/app/hooks/queries/useEvaluations';
import { useContext } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { useGetProjectsList } from '@/app/hooks/queries/useProjects';
import { Badge } from '@/app/ui/components/badge';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/app/ui/components/tooltip';
import { Kbd, KbdCommand, KbdGroup } from '@/app/ui/components/kbd';
import { FavoritesList } from '@/app/components/features/favorites/FavoritesList';
import { AccessRestrictedModal } from '@/app/components/features/projects/projectAccess/AccessRestrictedModal';
import { useNavigate } from '@tanstack/react-router';
import { useParams } from '@tanstack/react-router';
import { EvaluationBottomNavigationContent } from '@/app/components/features/evaluations/EvaluationBottomNavigationSectionContent';
import { Main } from '@/app/ui/components/main';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent } from '@/app/ui/components/error';
import { Icons } from '@/app/ui/components/icons';

export const Route = createFileRoute('/_authed/project/$projectId/_layout')({
  component: RouteComponent,
  loader: async (opts) => {
    const project = await opts.context.queryClient.ensureQueryData(
      getProjectDetailsQuery(opts.params.projectId),
    );
    if (!project) throw notFound();
  },
  pendingComponent: () => (
    <Main className="relative">
      <Spinner kind="inset" />
    </Main>
  ),
  errorComponent: (props) => {
    if (props.error instanceof AxiosError && props.error.response?.status === 403)
      return <AccessRestricted />;

    return <ErrorComponent {...props} />;
  },
});

function AccessRestricted() {
  const { userMeta } = use(AppContext);
  return (
    <>
      {userMeta && <NewIntercomMessenger loggedUser={userMeta} />}
      <AccessRestrictedModal />;
    </>
  );
}

function RouteComponent() {
  const { userMeta } = use(AppContext);
  const t = useTranslations();
  const matches = useMatches();
  const maybeParams = useParams({ strict: false });
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const evaluationId =
    maybeParams.tenderPackageId ?? maybeParams.contractId ?? maybeParams.earlyPhaseAssessmentId;
  const evaluationType = maybeParams.tenderPackageId
    ? EvaluationTypeLabelsEnum.TENDER_PACKAGE
    : maybeParams.contractId
      ? EvaluationTypeLabelsEnum.CONTRACT
      : EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT;

  return (
    <div className="flex grow min-w-0 bg-background">
      {userMeta && <NewIntercomMessenger loggedUser={userMeta} />}
      <Sidebar>
        <SidebarContent>
          <SidebarHeader>
            <SidebarOrganizationMenu />
            <Search />
            <SidebarTrigger />
          </SidebarHeader>
          <SidebarList>
            <SidebarItem>
              <SidebarLink to="/">
                <HugeiconsIcon icon={Building06Icon} />
                {t('pages.homepage.breadcrumbs.projects')}
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink
                to={'/notifications'}
                onClick={(e) => e.preventDefault()}
                className="hover:bg-transparent cursor-default"
              >
                <HugeiconsIcon icon={InboxIcon} />
                {t('pages.notifications.title')}
                <Badge size="xs" variant="tertiary" className="ml-auto text-muted">
                  {t('general.comingSoon')}
                </Badge>
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink
                to={'/tasks'}
                onClick={(e) => e.preventDefault()}
                className="hover:bg-transparent cursor-default"
              >
                <HugeiconsIcon icon={Task02Icon} />
                {t('pages.tasks.title')}
                <Badge size="xs" variant="tertiary" className="ml-auto text-muted">
                  {t('general.comingSoon')}
                </Badge>
              </SidebarLink>
            </SidebarItem>
          </SidebarList>
          <SidebarHeading>{t('components.projectSidebar.yourWorkspace')}</SidebarHeading>
          <SidebarAccordion defaultValue={[projectId]}>
            <SidebarAccordionItem value={projectId} className={'mb-2'}>
              <SidebarLink
                className="group/accordion-trigger-link"
                to="/project/$projectId"
                params={{ projectId }}
                data-inactive={
                  !matches.some((m) => m.routeId.includes('(home)')) ||
                  matches.some((m) => m.routeId.includes('(evaluation)'))
                    ? ''
                    : undefined
                }
              >
                <SidebarAccordionTrigger>
                  <ProjectTypeIcon type={project?.type ?? ProjectTypeLabelsEnum.Building} />
                </SidebarAccordionTrigger>
                <span className="line-clamp-1">{project?.name}</span>
              </SidebarLink>
              <SidebarAccordionPanel>
                <SidebarList className="pt-1">
                  <SidebarLink
                    to="/project/$projectId"
                    params={{ projectId }}
                    activeOptions={{ exact: true }}
                  >
                    <Icons.evaluations />
                    {t('components.projectSidebar.evaluations')}
                  </SidebarLink>
                  {evaluationId && (
                    <SidebarAccordion defaultValue={[evaluationId]}>
                      <SidebarAccordionItem value={evaluationId}>
                        <EvaluationSidebarSectionContent
                          evaluationType={evaluationType}
                          evaluationId={evaluationId}
                        />
                      </SidebarAccordionItem>
                    </SidebarAccordion>
                  )}
                  <SidebarLink to="/project/$projectId/insights" params={{ projectId }}>
                    <HugeiconsIcon icon={Chart01Icon} />
                    {t('components.projectSidebar.insights')}
                  </SidebarLink>
                </SidebarList>
              </SidebarAccordionPanel>
            </SidebarAccordionItem>
          </SidebarAccordion>
          <SidebarHeading>{t('components.projectSidebar.favorites')}</SidebarHeading>
          <FavoritesList />
          <SidebarUserMenu />
        </SidebarContent>
      </Sidebar>
      <Outlet />
      <BottomNavigation>
        <BottomNavigationList
          className="grid-cols-3 data-evaluation:grid-cols-4"
          data-evaluation={evaluationId ? '' : undefined}
        >
          <BottomNavigationLink to="/">
            <HugeiconsIcon icon={Building06Icon} />
            {t('pages.homepage.breadcrumbs.projects')}
          </BottomNavigationLink>
          {evaluationId ? (
            <EvaluationBottomNavigationContent
              evaluationType={evaluationType}
              evaluationId={evaluationId}
            />
          ) : (
            <>
              <BottomNavigationLink
                to="/project/$projectId"
                params={{ projectId }}
                activeOptions={{ exact: true }}
              >
                <Icons.evaluations />
                {t('components.projectSidebar.evaluations')}
              </BottomNavigationLink>
              <BottomNavigationLink to="/project/$projectId/insights">
                <HugeiconsIcon icon={Chart01Icon} />
                {t('components.projectSidebar.insights')}
              </BottomNavigationLink>
            </>
          )}
        </BottomNavigationList>
      </BottomNavigation>
    </div>
  );
}

type SearchItem = {
  value: string;
  label: string;
  type: 'evaluation' | 'project';
  onClick: () => void;
  evaluationType?: EvaluationTypeLabelsEnum;
};

type SearchGroup = {
  value: string;
  items: SearchItem[];
};

function Search() {
  const t = useTranslations();
  const navigate = useNavigate();
  const { userMeta } = useContext(AppContext);
  const { projectId } = Route.useParams();
  const [open, setOpen] = React.useState(false);
  const { data: evaluations } = useGetEvaluationsList(
    userMeta.organizationId ?? '',
    projectId,
    999,
  );
  const { data: projects } = useGetProjectsList(userMeta.organizationId ?? '');
  const [searchText, setSearchText] = React.useState('');

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const searchGroups: SearchGroup[] = React.useMemo(() => {
    const groups: SearchGroup[] = [];

    if (evaluations && evaluations.length > 0) {
      groups.push({
        value: 'evaluations',
        items: evaluations.map((evaluation) => {
          const evaluationId = evaluation.evaluationResourceId;

          return {
            value: `eval-${evaluation.evaluationId}`,
            label: evaluation.evaluationName,
            type: 'evaluation' as const,
            evaluationType: evaluation.evaluationType,
            onClick: () => {
              setOpen(false);
              if (evaluation.evaluationType === EvaluationTypeLabelsEnum.CONTRACT) {
                return navigate({
                  to: '/project/$projectId/contracts/$contractId',
                  params: { projectId, contractId: evaluationId },
                });
              }
              if (evaluation.evaluationType === EvaluationTypeLabelsEnum.TENDER_PACKAGE) {
                return navigate({
                  to: '/project/$projectId/tender-packages/$tenderPackageId',
                  params: { projectId, tenderPackageId: evaluationId },
                });
              }
              if (evaluation.evaluationType === EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT) {
                navigate({
                  to: '/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId',
                  params: { projectId, earlyPhaseAssessmentId: evaluationId },
                });
              }
            },
          };
        }),
      });
    }

    if (projects && projects.length > 0) {
      groups.push({
        value: 'projects',
        items: projects.map((project) => ({
          value: `project-${project.id}`,
          label: project.name,
          type: 'project' as const,
          onClick: () => {
            navigate({ to: '/project/$projectId', params: { projectId: project.id } });
            setOpen(false);
          },
        })),
      });
    }

    return groups;
  }, [evaluations, projects, navigate, projectId]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <CommandDialogTrigger
              render={
                <SidebarHeaderButton
                  aria-label="search"
                  className="ml-auto"
                  onClick={() => setOpen(true)}
                >
                  <HugeiconsIcon icon={Search01Icon} />
                </SidebarHeaderButton>
              }
            />
          }
        />
        <TooltipPopup>
          {t('components.globalSearch.search')}{' '}
          <KbdGroup>
            <KbdCommand />
            <Kbd>K</Kbd>
          </KbdGroup>
        </TooltipPopup>
      </Tooltip>
      <CommandDialogPopup>
        <Command items={searchGroups}>
          <div className="relative">
            <CommandInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('components.globalSearch.searchProjects')}
            />
            <KbdGroup className="absolute inset-y-0 my-auto right-3">
              <KbdCommand />
              <Kbd>K</Kbd>
            </KbdGroup>
          </div>
          <CommandPanel>
            <CommandEmpty className={'break-all'}>
              {t('components.globalSearch.noResults', { searchText })}
            </CommandEmpty>
            <CommandList>
              {(group: SearchGroup) => (
                <React.Fragment key={group.value}>
                  <CommandGroup items={group.items}>
                    <CommandGroupLabel>
                      {group.value === 'evaluations' && t('components.projectSidebar.evaluations')}
                      {group.value === 'projects' && t('pages.homepage.breadcrumbs.projects')}
                    </CommandGroupLabel>
                    <CommandCollection>
                      {(item: SearchItem) => {
                        const project = projects?.find((p) => `project-${p.id}` === item.value);
                        return (
                          <CommandItem key={item.value} onClick={item.onClick} value={item.value}>
                            {item.type === 'evaluation' && item.evaluationType && (
                              <EvaluationTypeIcon type={item.evaluationType} />
                            )}
                            {item.type === 'project' && project && (
                              <ProjectTypeIcon type={project.type} />
                            )}
                            <span className="line-clamp-1">{item.label}</span>
                          </CommandItem>
                        );
                      }}
                    </CommandCollection>
                  </CommandGroup>
                </React.Fragment>
              )}
            </CommandList>
          </CommandPanel>
          <CommandFooter>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <KbdGroup>
                  <Kbd>
                    <HugeiconsIcon icon={ArrowUpIcon} />
                  </Kbd>
                  <Kbd>
                    <HugeiconsIcon icon={ArrowDownIcon} />
                  </Kbd>
                </KbdGroup>
                <span>{t('general.navigate')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Kbd>
                  <HugeiconsIcon icon={ArrowTurnBackwardIcon} />
                </Kbd>
                <span>{t('general.open')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Kbd>Esc</Kbd>
              <span>{t('general.close')}</span>
            </div>
          </CommandFooter>
        </Command>
      </CommandDialogPopup>
    </CommandDialog>
  );
}
